<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import SalesPartyActionsMenu from './SalesPartyActionsMenu.vue';
import SalesPartyGroupToggle from './SalesPartyGroupToggle.vue';
import SalesPartyQuickAddCard from './SalesPartyQuickAddCard.vue';
import SalesPartyProgressBar from './SalesPartyProgressBar.vue';
import SalesPartyStatusChips from './SalesPartyStatusChips.vue';

defineProps({
  people: { type: Array, default: () => [] },
  progressStatus: { type: Function, required: true },
  roleSingle: { type: String, required: true },
});

const emit = defineEmits(['add', 'edit', 'remove']);
const { t } = useI18n();
const expanded = ref(true);
const toggle = () => {
  expanded.value = !expanded.value;
};
</script>

<template>
  <div>
    <section v-if="people.length" class="grid gap-3">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <h2 class="text-base font-semibold text-lais-text">
          {{ t('SALES_PARTIES.COMMON.PHYSICAL_PEOPLE') }}
        </h2>
        <div class="flex items-center gap-2">
          <span
            class="rounded-full bg-lais-bg-soft px-3 py-1 text-xs font-semibold text-lais-text-muted"
          >
            {{
              t('SALES_PARTIES.LIST.COUNT', {
                count: people.length,
                role: roleSingle,
              })
            }}
          </span>
          <SalesPartyGroupToggle
            data-test="people-group-toggle"
            :expanded="expanded"
            :group-label="t('SALES_PARTIES.COMMON.PHYSICAL_PEOPLE')"
            @toggle="toggle"
          />
        </div>
      </div>
      <div
        v-if="expanded"
        data-test="people-group-content"
        class="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
      >
        <LaisCard
          v-for="person in people"
          :key="person.id"
          :title="person.display_name"
          :prefix-icon="
            Number(person.progress?.percent) === 100
              ? 'i-lucide-check'
              : 'i-lucide-user-round'
          "
        >
          <template #titleAction>
            <SalesPartyActionsMenu
              @edit="emit('edit', person)"
              @remove="emit('remove', person)"
            />
          </template>
          <button
            type="button"
            class="grid w-full gap-3 text-left"
            @click="emit('edit', person)"
          >
            <div class="flex items-start justify-between gap-3">
              <SalesPartyStatusChips
                :percent="Number(person.progress?.percent || 0)"
                :status="progressStatus(person)"
                :type-label="t('SALES_PARTIES.LIST.PHYSICAL_BADGE')"
              />
              <span class="text-lais-primary">
                {{
                  t('SALES_PARTIES.COMMON.PERCENT', {
                    value: person.progress?.percent || 0,
                  })
                }}
              </span>
            </div>
            <SalesPartyProgressBar
              :label="person.display_name"
              :value="Number(person.progress?.percent || 0)"
            />
          </button>
        </LaisCard>
        <SalesPartyQuickAddCard
          :label="t('SALES_PARTIES.ACTIONS.ADD_PERSON')"
          @select="emit('add')"
        />
      </div>
    </section>
  </div>
</template>
